import { ORPCError } from "@orpc/client";
import { Button } from "@/components/ui/button.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { useSegmentsClient } from "@domains/segments/client/api/segmentsQuery.ts";
import type { SegmentDetail } from "@domains/segments/shared/segmentsTypes.ts";
import { EmptyState } from "@packages/core-client/collection/EmptyState.tsx";
import { ErrorState } from "@packages/core-client/errors/ErrorState.tsx";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { useSkeletonState } from "@packages/core-client/hooks/useSkeletonState.ts";
import { useI18nextInstance } from "@squide/i18next";
import { useQuery } from "@tanstack/react-query";
import { forwardRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { i18NextInstanceKey } from "../../i18n/i18next.ts";
import { CurrentFiltersForm } from "./CurrentFiltersForm.tsx";
import { TeamSegmentForm, type TeamSegmentFormHandle } from "./TeamSegmentForm.tsx";

const PLACEHOLDER_SEGMENT: SegmentDetail = { id: "skeleton", name: "Xxxxxxxxx xxxxxxxx", isRuleBased: true, isEditable: true, matchType: "all", rules: [], requiredRules: [], hasUnlistedRequiredRules: false, hasUnlistedRules: false, members: [], tags: [] };

export interface ManageSegmentsEditorPanelProps {
    panel: string;
    onDone: () => void;
    onCreated: (segmentId: string) => void;
    onApply: (segmentId: string) => void;
    onFilterApplied: () => void;
}

interface ExistingSegmentFormProps {
    segmentId: string;
    onDone: () => void;
    onApply: () => void;
    onFilterApplied: () => void;
}

const ExistingSegmentForm = forwardRef<TeamSegmentFormHandle, ExistingSegmentFormProps>(function ExistingSegmentForm({ segmentId, onDone, onApply, onFilterApplied }, ref) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("ManageSegmentsEditorPanel", { i18n: i18nextInstance });
    const { queryUtils } = useSegmentsClient();
    const session = useSession();

    const {
        data: segment,
        isPending,
        isError,
        error,
        refetch
    } = useQuery(
        createQueryOptions(queryUtils.getSegment.queryOptions({ input: { orgId: session?.organizationId ?? "", segmentId } }), {
            throwOnError: false,
            retry: (count, queryError) => !(queryError instanceof ORPCError && queryError.code === "NOT_FOUND") && count < 1
        })
    );

    const { hasContent, isLoading } = useSkeletonState({ isPending, data: segment ? [segment] : undefined, placeholder: [PLACEHOLDER_SEGMENT] });
    const handleRetry = useCallback(() => void refetch(), [refetch]);

    if (isError) {
        const isNotFound = error instanceof ORPCError && error.code === "NOT_FOUND";

        return (
            <div className="p-6">
                {isNotFound ? (
                    <ErrorState
                        isEmbedded
                        kind="notFound"
                        title={t("notFoundTitle")}
                        action={
                            <Button intent="outline" onPress={onDone}>
                                {t("backToList")}
                            </Button>
                        }
                    />
                ) : (
                    <ErrorState isEmbedded kind="load" title={t("loadError")} onRetry={handleRetry} retryLabel={t("retry")} />
                )}
            </div>
        );
    }

    return (
        <>
            <output className="sr-only">{isLoading && t("loading")}</output>

            {hasContent &&
                (isLoading || !segment ? (
                    <Skeleton isLoading inert aria-hidden="true" className="flex flex-col gap-8 p-6">
                        <div>
                            <div className="flex flex-col gap-2">
                                <span className="text-2xl">{PLACEHOLDER_SEGMENT.name}</span>
                            </div>
                            <div className="mt-8 flex flex-col gap-3">
                                <span>Xxx xxxxxxx xxx xxxxx</span>
                                <span>Xxxx-xxxxx</span>
                                <span>Xxxxxxxx xxxxxx</span>
                            </div>
                        </div>
                    </Skeleton>
                ) : (
                    <TeamSegmentForm ref={ref} key={segmentId} segment={segment} onDone={onDone} onApply={onApply} onFilterApplied={onFilterApplied} />
                ))}
        </>
    );
});

export const ManageSegmentsEditorPanel = forwardRef<TeamSegmentFormHandle, ManageSegmentsEditorPanelProps>(function ManageSegmentsEditorPanel({ panel, onDone, onCreated, onApply, onFilterApplied }, ref) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("ManageSegmentsEditorPanel", { i18n: i18nextInstance });
    const handleApply = useCallback(() => onApply(panel), [onApply, panel]);

    if (panel === "list") {
        return (
            <div className="p-6">
                <EmptyState isEmbedded title={t("emptyTitle")} description={t("emptyDescription")} />
            </div>
        );
    }

    if (panel === "new") {
        return <TeamSegmentForm ref={ref} onCreated={onCreated} onFilterApplied={onFilterApplied} />;
    }

    if (panel === "current-filters") {
        return <CurrentFiltersForm ref={ref} onApplied={onFilterApplied} />;
    }

    return <ExistingSegmentForm ref={ref} segmentId={panel} onDone={onDone} onApply={handleApply} onFilterApplied={onFilterApplied} />;
});
